import React, { useState, useRef, useEffect, useCallback, useContext } from 'react';
import { Checkbox, FormControlLabel, Grid } from "@mui/material";
import styles from "../../../Styles/ComponentStyles/AIPdfExtractor/AIPdfExtractor.module.scss";
import GlobalButton from "../../../Utils/GlobalUtilsComponents/GlobalButton";
import { ProfileContext, ProfileContextPayload } from '../../../Context/ProfileDetailsContext';

// Interfaces for type safety
interface Page {
    page_number: number;
    image_url: string;
}

const SelectPagesContainer = () => {

    const {
        extractedFileData,
        selectedPages,
        setSelectedPages
    } = useContext(ProfileContext) as ProfileContextPayload;

    // State management
    const [currentPreviewPage, setCurrentPreviewPage] = useState<number>(1);
    const [viewMode, setViewMode] = useState<'all' | 'selected'>('all');

    // Refs
    const previewSectionRef = useRef<HTMLDivElement>(null);
    const thumbnailSectionRef = useRef<HTMLDivElement>(null);
    const pageRefs = useRef<(HTMLDivElement | null)[]>([]);
    const thumbnailRefs = useRef<(HTMLDivElement | null)[]>([]);

    // Page selection handler
    const handlePageSelection = (pageNumber: number) => {
        setSelectedPages(prev =>
            prev.includes(pageNumber)
                ? prev.filter(p => p !== pageNumber)
                : [...prev, pageNumber]
        );
    };

    // Thumbnail click handler
    const handlePageThumbnailClick = (pageNumber: number) => {
        const pageIndex = pagesToDisplay.findIndex((page: Page) => page.page_number === pageNumber);

        if (pageIndex !== -1 && previewSectionRef.current) {
            const pageElement = pageRefs.current[pageIndex];
            if (!pageElement) return; // Exit early if the element is null

            const containerTop = previewSectionRef.current.getBoundingClientRect().top;
            const pageTop = pageElement.getBoundingClientRect().top;
            const currentScroll = previewSectionRef.current.scrollTop;
            const scrollTo = currentScroll + (pageTop - containerTop);

            previewSectionRef.current.scrollTop = scrollTo;
            setCurrentPreviewPage(pageNumber);
        }
    };


    // View mode toggle handler
    const handleViewSelectedPages = () => {
        setViewMode(prev => prev === 'all' ? 'selected' : 'all');
    };

    // Determine pages to display based on view mode
    const pagesToDisplay = viewMode === 'all'
        ? extractedFileData?.file_pages || []
        : (extractedFileData?.file_pages || []).filter((page: any) =>
            selectedPages.includes(page.page_number)
        );

    // Scroll event handler with debounce
    const handlePreviewScroll = useCallback(() => {
        if (!previewSectionRef.current || !thumbnailSectionRef.current) return;

        const scrollTop = previewSectionRef.current.scrollTop;
        let mostVisiblePage = 1;
        let maxVisibility = 0;

        pageRefs.current.forEach((pageRef, index) => {
            if (!pageRef) return;

            const pageTop = pageRef.offsetTop;
            const pageHeight = pageRef.offsetHeight;
            const pageBottom = pageTop + pageHeight;

            // Calculate how much of the page is visible
            const visibleTop = Math.max(scrollTop, pageTop);
            const visibleBottom = Math.min(scrollTop + previewSectionRef.current!.clientHeight, pageBottom);
            const visibleHeight = Math.max(0, visibleBottom - visibleTop);

            // Update most visible page
            if (visibleHeight > maxVisibility) {
                maxVisibility = visibleHeight;
                // Get the actual page number from the displayed page
                const actualPageNumber = pagesToDisplay[index]?.page_number || 1;
                mostVisiblePage = actualPageNumber;
            }
        });

        // Update current preview page
        setCurrentPreviewPage(mostVisiblePage);

        // Find the thumbnail index (might be different from page index in selected view)
        const thumbnailIndex = pagesToDisplay.findIndex((page: Page) => page.page_number === mostVisiblePage);

        // Scroll thumbnail section to match
        if (thumbnailIndex !== -1) {
            const thumbnailElement = thumbnailRefs.current[thumbnailIndex];
            if (thumbnailElement && thumbnailSectionRef.current) {
                const thumbnailTop = thumbnailElement.offsetTop;
                const thumbnailHeight = thumbnailElement.offsetHeight;
                const containerHeight = thumbnailSectionRef.current.clientHeight;
                const scrollOffset = thumbnailTop - (containerHeight / 2) + (thumbnailHeight / 2);

                thumbnailSectionRef.current.scrollTo({
                    top: scrollOffset,
                    behavior: 'smooth'
                });
            }
        }
    }, [pagesToDisplay]);

    // Reset page refs when pages change
    useEffect(() => {
        pageRefs.current = pageRefs.current.slice(0, pagesToDisplay.length);
        thumbnailRefs.current = thumbnailRefs.current.slice(0, pagesToDisplay.length);
    }, [pagesToDisplay]);

    useEffect(() => {
        // When switching view modes, ensure the current page is still visible
        if (viewMode === 'selected' && !selectedPages.includes(currentPreviewPage)) {
            // If current page isn't in selected pages, set to first selected page
            if (selectedPages.length > 0) {
                setCurrentPreviewPage(selectedPages[0]);
            }
        }

        // Reset the scroll position to show the current page
        if (pagesToDisplay.length > 0) {
            const currentPageIndex = pagesToDisplay.findIndex(
                (page: Page) => page.page_number === currentPreviewPage
            );

            if (currentPageIndex !== -1 && pageRefs.current[currentPageIndex]) {
                setTimeout(() => {
                    handlePageThumbnailClick(currentPreviewPage);
                }, 0);
            } else if (pagesToDisplay.length > 0) {
                // If current page isn't visible, show the first page
                setCurrentPreviewPage(pagesToDisplay[0].page_number);
                setTimeout(() => {
                    handlePageThumbnailClick(pagesToDisplay[0].page_number);
                }, 0);
            }
        }
    }, [viewMode, pagesToDisplay]);

    return (
        <Grid container>
            {/* Left side - Page Selection Section */}
            <Grid item xs={3} height="75vh" className={styles.selectPageSection}>
                <Grid
                    container
                    gap={1}
                    ref={thumbnailSectionRef}
                    style={{
                        height: '100%',
                        overflowY: 'auto'
                    }}
                >
                    {/* View Selected/All Pages Button */}
                    <Grid
                        item
                        xs={12}
                        sx={{
                            top: 0,
                            position: 'sticky',
                            zIndex: 10,
                            backgroundColor: "rgb(88, 84, 84)",
                            padding: "10px 20px"
                        }}
                    >
                        <GlobalButton
                            value={viewMode === 'all' ? "View Selected Pages" : "View All Pages"}
                            size="medium"
                            className={styles.selectedAndAllPageBtn}
                            disabled={viewMode === 'all' ? selectedPages.length === 0 : false}
                            onClick={handleViewSelectedPages}
                        />
                    </Grid>

                    {/* Page Thumbnails */}
                    <Grid item xs={12} padding={"20px"}>
                        <Grid container gap={3}>
                            {(viewMode === 'all'
                                ? extractedFileData?.file_pages || []
                                : (extractedFileData?.file_pages || []).filter((page: any) =>
                                    selectedPages.includes(page.page_number)
                                )
                            ).map((page: Page, index: number) => (
                                <>
                                    <Grid
                                        item
                                        xs={12}
                                        key={page.page_number}
                                        ref={(el) => thumbnailRefs.current[index] = el}
                                    >
                                        <Grid
                                            container
                                            alignItems="center"
                                            gap={4}
                                            justifyContent="center"
                                            style={{
                                                cursor: 'pointer',
                                                opacity: viewMode === 'selected' && !selectedPages.includes(page.page_number) ? 0.5 : 1
                                            }}
                                        >
                                            {/* Checkbox for page selection */}
                                            <Grid item>
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={selectedPages.includes(page.page_number)}
                                                            onChange={() => handlePageSelection(page.page_number)}
                                                        />
                                                    }
                                                    label=""
                                                    className={styles.pageSelectionCheckbox}
                                                />
                                            </Grid>

                                            {/* Page Thumbnail */}
                                            <Grid
                                                item
                                                onClick={() => handlePageThumbnailClick(page.page_number)}
                                            >
                                                <Grid container gap="5px">
                                                    <Grid item xs={12}>
                                                        <img
                                                            width="250px"
                                                            height="300px"
                                                            src={page?.image_url}
                                                            alt={`PDF page ${page.page_number}`}
                                                            className={`${styles.pageThumbnail} ${currentPreviewPage === page.page_number ? styles.activePageThumbnail : ''
                                                                }`}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} className={styles.pageNumberText}>
                                                        {page.page_number}
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </>
                            ))}
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>

            {/* Right side - Preview Section */}
            <Grid
                item
                xs={9}
                height="75vh"
                className={`${styles.previewSection} ${styles.customScrollbarForPreview}`}
                sx={{
                    scrollbarColor: '#888 rgba(217, 217, 217, 1)'
                }}
            >
                <Grid
                    container
                    gap={3}
                    className={styles.previewContainer}
                    ref={previewSectionRef}
                    onScroll={handlePreviewScroll}
                    style={{
                        height: '100%',
                        overflowY: 'auto',
                        position: 'relative',
                    }}
                >
                    {pagesToDisplay.map((page: Page, index: number) => (
                        <Grid
                            xs={12}
                            item
                            key={page.page_number}
                            ref={(el) => pageRefs.current[index] = el}
                            data-page-number={page.page_number}
                        >
                            <img
                                src={page?.image_url}
                                alt={`PDF page ${page.page_number}`}
                                style={{
                                    width: '80%',
                                    display: 'block',
                                    margin: '0 auto'
                                }}
                            // width={900}
                            // height={800}
                            />
                        </Grid>
                    ))}
                </Grid>
            </Grid>
        </Grid>
    );
};

export default SelectPagesContainer;